import { ChevronRight } from 'lucide-react'
import { fd } from '@/lib/helpers'
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip'
import { BarChart3 } from 'lucide-react'

const STATUS_MAP: Record<string, { lbl: string; bg: string; fg: string }> = {
  planejamento: { lbl: 'Planejamento', bg: 'hsl(var(--sky-bg))', fg: 'hsl(var(--sky))' },
  em_execucao: { lbl: 'Execução', bg: 'hsl(var(--amber-bg))', fg: 'hsl(var(--amber))' },
  pausada: { lbl: 'Pausada', bg: 'hsl(var(--orange-bg))', fg: 'hsl(var(--orange))' },
  concluida: { lbl: 'Concluída', bg: 'hsl(var(--emerald-bg))', fg: 'hsl(var(--emerald))' },
  cancelada: { lbl: 'Cancelada', bg: 'hsl(var(--rose-bg))', fg: 'hsl(var(--rose))' },
}

function calcSemaforo(o: any): { emoji: string; label: string; cor: string } {
  if (o.status === 'concluida') return { emoji: '🟢', label: 'Concluída', cor: 'var(--emerald)' }
  if (o.status === 'cancelada' || o.status === 'pausada') return { emoji: '⚪', label: o.status, cor: 'var(--txt2)' }

  if (!o.data_termino_prev) return { emoji: '⚪', label: 'Sem prazo', cor: 'var(--txt2)' }

  const now = new Date()
  const fim = new Date(o.data_termino_prev)
  const diasRestantes = Math.ceil((fim.getTime() - now.getTime()) / 86400000)
  const pct = o.avanco_fisico || 0

  // Past due
  if (diasRestantes < 0) return { emoji: '🔴', label: 'Atrasada', cor: 'var(--rose)' }

  // Has deadline but 0% progress
  if (pct === 0 && o.status === 'em_execucao') return { emoji: '🔴', label: 'Em risco', cor: 'var(--rose)' }

  // Calculate expected progress
  const inicio = o.data_inicio ? new Date(o.data_inicio) : null
  if (inicio) {
    const totalDias = Math.max(1, (fim.getTime() - inicio.getTime()) / 86400000)
    const diasPassados = Math.max(0, (now.getTime() - inicio.getTime()) / 86400000)
    const pctEsperado = Math.min(100, (diasPassados / totalDias) * 100)
    const desvio = pctEsperado - pct

    if (desvio > 20) return { emoji: '🔴', label: 'Em risco', cor: 'var(--rose)' }
    if (desvio > 10) return { emoji: '🟡', label: 'Atenção', cor: 'var(--amber)' }
  } else if (diasRestantes < 30 && pct < 80) {
    return { emoji: '🟡', label: 'Atenção', cor: 'var(--amber)' }
  }

  return { emoji: '🟢', label: 'No prazo', cor: 'var(--emerald)' }
}

function calcDiasRestantes(o: any): string {
  if (!o.data_termino_prev) return '—'
  const dias = Math.ceil((new Date(o.data_termino_prev).getTime() - Date.now()) / 86400000)
  if (dias < 0) return `${Math.abs(dias)}d atrás`
  if (dias === 0) return 'Hoje'
  return `${dias}d`
}

function calcProjecao(o: any): string | null {
  if (!o.data_inicio || !o.data_termino_prev) return null
  const pct = o.avanco_fisico || 0
  if (pct <= 0) return null

  const inicio = new Date(o.data_inicio)
  const diasDecorridos = Math.max(1, (Date.now() - inicio.getTime()) / 86400000)
  const ritmoDiario = pct / diasDecorridos
  if (ritmoDiario <= 0) return null

  const diasRestantes = (100 - pct) / ritmoDiario
  const projecao = new Date(Date.now() + diasRestantes * 86400000)
  return projecao.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' })
}

interface Props {
  obraDetails: any[]
  fvsPorObra: Record<string, number>
  reqPorObra: Record<string, number>
  onObraClick: (obra: any) => void
}

export default function DashboardObrasTable({ obraDetails, fvsPorObra, reqPorObra, onObraClick }: Props) {
  return (
    <TooltipProvider>
      <div className="rounded-xl border overflow-hidden" style={{ background: 'hsl(var(--bg2))', borderColor: 'hsl(var(--line))' }}>
        <div className="px-4 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid hsl(var(--line))' }}>
          <BarChart3 size={14} className="text-primary" />
          <span className="text-sm font-bold text-foreground">Obras — Visão Gerencial</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr style={{ background: 'hsl(var(--bg3))' }}>
                {['Saúde', 'Obra', 'Status', 'Avanço', 'Dias Rest.', 'Prazo', 'Responsável', 'FVS Pend.', 'Req. Abertas', 'Oc. Crít.', 'Pend. Abertas', 'Último RDO'].map(h => (
                  <th key={h} className="px-3 py-2.5 text-left font-bold text-muted-foreground uppercase tracking-wider text-[10px]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {obraDetails.map(o => {
                const sem = calcSemaforo(o)
                const diasRest = calcDiasRestantes(o)
                const proj = calcProjecao(o)
                const atrasada = o.status === 'em_execucao' && o.data_termino_prev && new Date(o.data_termino_prev) < new Date()
                const semRdoRecente = o._lastRdoDays > 3 && o.status === 'em_execucao'
                const rowBg = sem.emoji === '🔴' ? 'hsl(var(--rose) / 0.06)' : sem.emoji === '🟡' ? 'hsl(var(--amber) / 0.04)' : 'transparent'
                const pct = o.avanco_fisico || 0
                const pctColor = pct >= 70 ? 'var(--emerald)' : pct >= 30 ? 'var(--amber)' : 'var(--rose)'
                const st = STATUS_MAP[o.status] || { lbl: o.status, bg: 'hsl(var(--bg3))', fg: 'hsl(var(--txt2))' }

                return (
                  <tr key={o.id} className="cursor-pointer hover:brightness-95 transition-all"
                    style={{ background: rowBg, borderBottom: '1px solid hsl(var(--line))' }}
                    onClick={() => onObraClick(o)}>
                    <td className="px-3 py-2.5 text-center">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-base cursor-help">{sem.emoji}</span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-xs">{sem.label}{proj ? ` · Projeção: ${proj}` : ''}</p>
                        </TooltipContent>
                      </Tooltip>
                    </td>
                    <td className="px-3 py-2.5 font-bold text-foreground max-w-[160px] truncate">{o.nome}</td>
                    <td className="px-3 py-2.5">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: st.bg, color: st.fg }}>{st.lbl}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="w-14 h-1.5 rounded-full overflow-hidden" style={{ background: 'hsl(var(--bg5))' }}>
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: `hsl(${pctColor})` }} />
                        </div>
                        <span className="font-bold" style={{ color: `hsl(${pctColor})` }}>{pct}%</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 font-semibold" style={{ color: diasRest.includes('atrás') ? 'hsl(var(--rose))' : 'hsl(var(--txt2))' }}>
                      {diasRest}
                    </td>
                    <td className="px-3 py-2.5 text-muted-foreground">{o.data_termino_prev ? fd(o.data_termino_prev) : '—'}</td>
                    <td className="px-3 py-2.5 text-muted-foreground">{o._engenheiro || '—'}</td>
                    <td className="px-3 py-2.5">
                      <span className="font-bold" style={{ color: (fvsPorObra[o.id] || 0) > 0 ? 'hsl(var(--amber))' : 'hsl(var(--emerald))' }}>
                        {fvsPorObra[o.id] || 0}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="font-bold" style={{ color: (reqPorObra[o.id] || 0) > 0 ? 'hsl(var(--amber))' : 'hsl(var(--emerald))' }}>
                        {reqPorObra[o.id] || 0}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="font-bold" style={{ color: (o.ocorrencias_criticas || 0) > 0 ? 'hsl(var(--rose))' : 'hsl(var(--emerald))' }}>
                        {o.ocorrencias_criticas || 0}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="font-bold" style={{ color: (o.pendencias_abertas || 0) > 0 ? 'hsl(var(--amber))' : 'hsl(var(--emerald))' }}>
                        {o.pendencias_abertas || 0}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-muted-foreground">
                      {o._lastRdoDays === 999 ? '—' : o._lastRdoDays === 0 ? 'Hoje' : `${o._lastRdoDays}d atrás`}
                      {semRdoRecente && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="ml-1 text-[9px] font-bold cursor-help" style={{ color: 'hsl(var(--amber))' }}>⚠ {o.nome}</span>
                          </TooltipTrigger>
                          <TooltipContent><p className="text-xs">Sem RDO há {o._lastRdoDays} dias</p></TooltipContent>
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                )
              })}
              {obraDetails.length === 0 && (
                <tr><td colSpan={12} className="px-3 py-8 text-center text-muted-foreground">Nenhuma obra cadastrada</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </TooltipProvider>
  )
}
